import { getSupabaseBaseUrl, supabase as defaultSupabase } from "@/services/supabaseClient";
import {
  loadClinicianAppointmentPatientIdentities,
  PATIENT_AVATAR_PLACEHOLDER,
} from "@/services/patientIdentityService.js";
import { pickDiagnosisNotes, pickDiagnosisTitle } from "@/utils/diagnosis.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";

const ICON_LOCATION = "/icons/doctor/home_content_media/distance.svg";
const ICON_VIDEO = "/icons/list-items/emergency_recording_Fill.svg";
const ICON_PENDING = "/icons/doctor/appointments/Pending.svg";
const DEFAULT_FACILITY_NAME = "Primary facility not set";

function safeDate(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toDateKeyLocal(value) {
  const d = safeDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function hhmmFromDate(value) {
  const d = safeDate(value);
  if (!d) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function plusMinutesHHMM(hhmm, minutesToAdd = 0) {
  const [hRaw, mRaw] = String(hhmm || "").split(":");
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const start = h * 60 + m;
  const end = start + Number(minutesToAdd || 0);
  const wrapped = ((end % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

function consultationLabel(consultationType) {
  return String(consultationType || "").toLowerCase() === "private"
    ? "Virtual"
    : "Consultancy";
}

function consultationIcon(row) {
  const status = String(row?.status || "").toLowerCase();
  if (status === "in_progress" || status === "pending") return ICON_PENDING;
  return String(row?.consultation_type || "").toLowerCase() === "private"
    ? ICON_VIDEO
    : ICON_LOCATION;
}

function normalizeGender(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function coerceBoolean(value) {
  if (typeof value === "boolean") return value;
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  if (["true", "yes", "y", "1"].includes(raw)) return true;
  if (["false", "no", "n", "0"].includes(raw)) return false;
  return null;
}

function normalizeConditions(value) {
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item || "").trim()).filter(Boolean);
    return items.length > 0 ? items.join(", ") : null;
  }
  const raw = String(value || "").trim();
  return raw || null;
}

function calcAge(dateOfBirth) {
  const dob = safeDate(dateOfBirth);
  if (!dob) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 && age <= 120 ? age : null;
}

function firstNonEmpty(values, fallback = "") {
  for (const v of values) {
    if (v === 0) return v;
    if (v === false) return v;
    if (v != null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function parseAmount(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const match = value.match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function formatAmount(amount, currency = "ZAR") {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return "";
  return `${numeric.toFixed(2)} ${String(currency || "ZAR").trim() || "ZAR"}`;
}

function parseFeesObject(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function pickFeeFromFeesObject(fees, mode = "default") {
  if (!fees || typeof fees !== "object") return null;
  const candidates =
    mode === "evisit"
      ? [
          fees.evisit_price,
          fees.e_visit_price,
          fees.virtual_price,
          fees.telehealth_price,
          fees.consultation_fee,
          fees.consultation_price,
          fees.default_fee,
          fees.default_price,
          fees.price,
        ]
      : [
          fees.consultation_fee,
          fees.consultation_price,
          fees.default_fee,
          fees.default_price,
          fees.in_person_price,
          fees.price,
        ];
  for (const candidate of candidates) {
    const amount = parseAmount(candidate);
    if (amount != null) return amount;
  }
  return null;
}

function pickCurrencyFromFeesObject(fees, mode = "default") {
  if (!fees || typeof fees !== "object") return "";
  const candidates =
    mode === "evisit"
      ? [
          fees.evisit_currency,
          fees.e_visit_currency,
          fees.virtual_currency,
          fees.telehealth_currency,
          fees.currency,
          fees.consultation_currency,
          fees.default_currency,
        ]
      : [
          fees.in_person_currency,
          fees.currency,
          fees.consultation_currency,
          fees.default_currency,
        ];
  return String(firstNonEmpty(candidates, "") || "").trim();
}

function readJSONStorage(keys = []) {
  if (typeof window === "undefined") return null;
  const list = Array.isArray(keys) ? keys : [keys];
  for (const key of list.filter(Boolean)) {
    try {
      const sessionRaw = window.sessionStorage.getItem(key);
      if (sessionRaw) return JSON.parse(sessionRaw);
    } catch {}
    try {
      const localRaw = window.localStorage.getItem(key);
      if (localRaw) return JSON.parse(localRaw);
    } catch {}
  }
  return null;
}

function readStoredDoctorAppointmentDraft(appointmentId) {
  if (typeof window === "undefined" || !appointmentId) return null;
  return readJSONStorage(`afrimed:doctor_appt:${String(appointmentId)}`);
}

function readStoredPatientAppointmentDraft(appointmentId) {
  if (typeof window === "undefined" || !appointmentId) return null;
  const scoped = readJSONStorage(`afrimed:appointment:${String(appointmentId)}`);
  if (scoped) return scoped;

  const parsed = readJSONStorage("afrimed:appointment:last");
  return String(parsed?.appointmentId || "") === String(appointmentId) ? parsed : null;
}

function combineStoredAppointmentDrafts(appointmentId) {
  const patientDraft = readStoredPatientAppointmentDraft(appointmentId);
  const doctorDraft = readStoredDoctorAppointmentDraft(appointmentId);
  if (!patientDraft && !doctorDraft) return null;

  return {
    ...(patientDraft || {}),
    ...(doctorDraft || {}),
    patient:
      doctorDraft?.patient ||
      patientDraft?.patient ||
      patientDraft?.restored?.patient ||
      null,
    doctor:
      doctorDraft?.doctor ||
      patientDraft?.doctor ||
      null,
    facility:
      doctorDraft?.facility ||
      patientDraft?.facility ||
      null,
    restored: {
      ...(patientDraft?.restored || {}),
      ...(doctorDraft?.restored || {}),
    },
  };
}

function extractStoredPatient(storedDraft) {
  if (!storedDraft || typeof storedDraft !== "object") return null;
  return (
    storedDraft?.patient ||
    storedDraft?.restored?.patient ||
    storedDraft?.restored?.patientProfile ||
    null
  );
}

function mergePatientContextWithStoredDraft(patientContext, storedDraft) {
  const storedPatient = extractStoredPatient(storedDraft);
  if (!storedPatient && !storedDraft) return patientContext;

  const mergedProfile = {
    ...(storedPatient && typeof storedPatient === "object" ? storedPatient : {}),
    ...(patientContext.patientProfile || {}),
  };

  const mergedAuthProfile = {
    ...(storedPatient && typeof storedPatient === "object" ? storedPatient : {}),
    ...(patientContext.authProfile || {}),
  };

  const mergedDob = firstNonEmpty(
    [
      patientContext.dob,
      storedPatient?.date_of_birth,
      storedPatient?.dob,
      storedDraft?.date_of_birth,
      storedDraft?.dob,
      null,
    ],
    null
  );
  const mergedGender = normalizeGender(
    firstNonEmpty(
      [
        patientContext.gender,
        storedPatient?.gender,
        storedPatient?.sex,
        storedDraft?.gender,
        storedDraft?.sex,
        "",
      ],
      ""
    )
  );
  const mergedAvatar = firstNonEmpty(
    [
      patientContext.avatar !== "/icons/circular-avatar-placeholder.svg" ? patientContext.avatar : "",
      resolveStorageAvatar(
        storedPatient?.profile_image_url || storedPatient?.avatar_url,
        storedPatient?.profile_photo_path || storedPatient?.avatar_path,
        ""
      ),
      storedDraft?.patient_avatar,
      "/icons/circular-avatar-placeholder.svg",
    ],
    "/icons/circular-avatar-placeholder.svg"
  );
  const mergedName = firstNonEmpty(
    [
      patientContext.patientName !== "Patient" ? patientContext.patientName : "",
      storedPatient?.full_name,
      storedPatient?.name,
      storedDraft?.patient_name,
      storedDraft?.patientName,
    ],
    "Patient"
  );

  const storedPhotoPaths = Array.isArray(storedPatient?.patient_photo_paths)
    ? storedPatient.patient_photo_paths.map((path) => String(path || "").trim()).filter(Boolean)
    : [];

  return {
    ...patientContext,
    patientRowId: firstNonEmpty(
      [
        patientContext.patientRowId,
        storedPatient?.patient_id,
        storedDraft?.patient_row_id,
        storedDraft?.patientId,
        storedDraft?.patient_id,
        null,
      ],
      null
    ),
    patientUserId: firstNonEmpty(
      [
        patientContext.patientUserId,
        storedPatient?.user_id,
        storedPatient?.id,
        storedDraft?.patient_user_id,
        storedDraft?.patientId,
        storedDraft?.patient_id,
        null,
      ],
      null
    ),
    patientProfile: mergedProfile,
    authProfile: mergedAuthProfile,
    patientName: mergedName,
    age: patientContext.age ?? calcAge(mergedDob),
    gender: mergedGender,
    dob: mergedDob,
    avatar: mergedAvatar,
    verificationStatus: normalizeVerificationStatus(
      firstNonEmpty(
        [
          patientContext.verificationStatus,
          storedPatient?.id_verification_status,
          storedPatient?.verification_status,
          storedDraft?.id_verification_status,
          storedDraft?.verification_status,
          "",
        ],
        ""
      ),
      ""
    ),
    patientPhotoPaths:
      patientContext.patientPhotoPaths?.length > 0
        ? patientContext.patientPhotoPaths
        : storedPhotoPaths,
  };
}

function isPlaceholderAvatar(value) {
  const avatar = String(value || "").trim();
  if (!avatar) return true;
  return (
    avatar === "/icons/circular-avatar-placeholder.svg" || avatar === PATIENT_AVATAR_PLACEHOLDER
  );
}

function pickPreferredAvatar(...candidates) {
  const values = candidates.map((value) => String(value || "").trim()).filter(Boolean);
  const meaningful = values.find((value) => !isPlaceholderAvatar(value));
  return meaningful || values[0] || "/icons/circular-avatar-placeholder.svg";
}

function mergeResolvedPatientContext(baseContext, resolvedContext) {
  if (!resolvedContext) return baseContext;
  if (!baseContext) return resolvedContext;

  const mergedProfile = {
    ...(baseContext.patientProfile || {}),
    ...(resolvedContext.patientProfile || {}),
  };
  const mergedAuthProfile = {
    ...(baseContext.authProfile || {}),
    ...(resolvedContext.authProfile || {}),
  };
  const mergedDob = firstNonEmpty(
    [
      resolvedContext.dob,
      baseContext.dob,
      mergedProfile.date_of_birth,
      mergedProfile.dob,
      null,
    ],
    null
  );
  const mergedGender = normalizeGender(
    firstNonEmpty(
      [
        resolvedContext.gender,
        baseContext.gender,
        mergedProfile.gender,
        mergedProfile.sex,
        "",
      ],
      ""
    )
  );

  return {
    ...baseContext,
    ...resolvedContext,
    patientRowId: firstNonEmpty(
      [resolvedContext.patientRowId, baseContext.patientRowId, null],
      null
    ),
    patientUserId: firstNonEmpty(
      [resolvedContext.patientUserId, baseContext.patientUserId, null],
      null
    ),
    patientProfile: mergedProfile,
    authProfile: mergedAuthProfile,
    patientName:
      resolvedContext.patientName && resolvedContext.patientName !== "Patient"
        ? resolvedContext.patientName
        : baseContext.patientName,
    age: resolvedContext.age ?? baseContext.age ?? calcAge(mergedDob),
    gender: mergedGender,
    dob: mergedDob,
    avatar: pickPreferredAvatar(
      resolvedContext.avatar,
      resolvedContext.patientAvatar,
      baseContext.avatar,
      baseContext.patientAvatar
    ),
    verificationStatus: normalizeVerificationStatus(
      firstNonEmpty(
        [
          resolvedContext.verificationStatus,
          baseContext.verificationStatus,
          mergedProfile.id_verification_status,
          mergedProfile.verification_status,
          "",
        ],
        ""
      ),
      ""
    ),
    patientPhotoPaths:
      Array.isArray(resolvedContext.patientPhotoPaths) && resolvedContext.patientPhotoPaths.length > 0
        ? resolvedContext.patientPhotoPaths
        : baseContext.patientPhotoPaths || [],
  };
}

function resolveDoctorAppointmentSubtitle(row, storedDraft, visitOrdinal = 1, resolvedServiceName = "") {
  const bookedServiceName = String(row?.booked_service_name || "").trim();
  if (
    bookedServiceName &&
    !/^(consult(?:ation|ancy)|first time consultancy)$/i.test(bookedServiceName)
  ) {
    return bookedServiceName;
  }

  const visitType = String(row?.consultation_type || "").toLowerCase();
  if (visitType === "private") return "Virtual";

  const explicitServiceName = String(resolvedServiceName || "").trim();
  if (
    explicitServiceName &&
    !/^(consult(?:ation|ancy)|first time consultancy)$/i.test(explicitServiceName)
  ) {
    return explicitServiceName;
  }

  const storedServiceName = String(
    firstNonEmpty(
      [
        storedDraft?.serviceName,
        storedDraft?.service_name,
        storedDraft?.serviceLabel,
        storedDraft?.payload?.serviceName,
        "",
      ],
      ""
    )
  ).trim();

  if (
    storedServiceName &&
    !/^(consult(?:ation|ancy)|first time consultancy)$/i.test(storedServiceName)
  ) {
    return storedServiceName;
  }
  if (Number(visitOrdinal || 1) <= 1) return "First time consultancy";
  return "Consultancy";
}

function resolveFacilityContext(row, doctorContext, storedDraft, fallbackFacilityName = DEFAULT_FACILITY_NAME) {
  const rawDoctorId = String(row?.doctor_id || "").trim();
  const doctorId = doctorContext.profileIdToUserId.get(rawDoctorId) || rawDoctorId;
  const practiceId = String(row?.practice_id || "").trim();

  const consultancy =
    (practiceId && doctorContext.consultanciesById.get(practiceId)) ||
    doctorContext.firstConsultancyByDoctor.get(doctorId) ||
    null;

  if (consultancy) {
    return {
      id: consultancy?.id || row?.practice_id || null,
      name: consultancy?.facility_name || fallbackFacilityName,
      address: consultancy?.facility_address || "",
      city: consultancy?.facility_city || "",
      country: consultancy?.facility_country || "",
    };
  }

  const legacy = doctorContext.legacyByDoctor.get(doctorId) || null;
  if (legacy) {
    return {
      id: row?.practice_id || legacy?.id || null,
      name: legacy?.facility_name || fallbackFacilityName,
      address: legacy?.street_address || "",
      city: legacy?.city || "",
      country: legacy?.country || "",
    };
  }

  if (storedDraft?.facility || storedDraft?.facilityName) {
    return {
      id: storedDraft?.facilityId || storedDraft?.facility?.id || row?.practice_id || null,
      name: storedDraft?.facilityName || storedDraft?.facility?.name || fallbackFacilityName,
      address: storedDraft?.facility?.address || "",
      city: storedDraft?.facility?.city || "",
      country: storedDraft?.facility?.country || "",
    };
  }

  return {
    id: row?.practice_id || null,
    name: fallbackFacilityName,
    address: "",
    city: "",
    country: "",
  };
}

function normalizeDoctorDisplayName(value, fallback = "Doctor") {
  const resolved = resolvePatientFacingDoctorName(value);
  return resolved === "Doctor" ? fallback : resolved;
}

function resolveDoctorStorageAvatar(url, path, fallback = "/images/demo/doctors/avatar-fallback.jpg") {
  const direct = String(url || "").trim();
  if (direct) return direct;

  const cleanPath = String(path || "").trim();
  if (cleanPath) {
    if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
    const base = getSupabaseBaseUrl();
    if (base) {
      return `${base}/storage/v1/object/public/doctor-profile-images/${cleanPath.replace(/^public\//, "")}`;
    }
  }

  return fallback;
}

function formatDoctorYears(value, fallback = "—") {
  const text = String(value ?? "").trim();
  if (!text) return fallback;
  if (/year/i.test(text)) return text;

  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric > 0) {
    return `${numeric} years`;
  }

  return text;
}

function formatDoctorRating(value, fallback = "—") {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric.toFixed(1) : fallback;
}

function isWeakDoctorName(value) {
  const text = String(value || "").trim().toLowerCase();
  return !text || text === "doctor";
}

function pickDoctorDisplayName(...candidates) {
  for (const candidate of candidates) {
    const resolved = normalizeDoctorDisplayName(candidate, "Doctor");
    if (!isWeakDoctorName(resolved)) return resolved;
  }
  return "Doctor";
}

function toDoctorSpecialtyText(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
  }
  return String(value || "").trim();
}

function isWeakDoctorSpecialty(value) {
  const text = toDoctorSpecialtyText(value).toLowerCase();
  return (
    !text ||
    text === "doctor" ||
    text === "general practitioner" ||
    text === "dermatologist"
  );
}

function pickDoctorSpecialty(...candidates) {
  for (const candidate of candidates) {
    const text = toDoctorSpecialtyText(candidate);
    if (!isWeakDoctorSpecialty(text)) return text;
  }
  return "Specialist";
}

function buildDoctorSummary(row, doctorContext, facility, storedDraft) {
  const rawDoctorId = String(row?.doctor_id || "").trim();
  const doctorId = doctorContext.profileIdToUserId.get(rawDoctorId) || rawDoctorId;
  const doctorProfile =
    doctorContext.doctorProfilesByDoctorId?.get(doctorId) ||
    doctorContext.doctorProfilesByDoctorId?.get(rawDoctorId) ||
    null;
  const authProfile =
    doctorContext.authProfilesByDoctorId?.get(doctorId) ||
    doctorContext.authProfilesByDoctorId?.get(rawDoctorId) ||
    null;
  const publicDoctor =
    doctorContext.publicDoctorsByDoctorId?.get(doctorId) ||
    doctorContext.publicDoctorsByDoctorId?.get(rawDoctorId) ||
    null;
  const fallbackDoctor =
    storedDraft?.doctor && typeof storedDraft.doctor === "object" ? storedDraft.doctor : null;

  const verificationStatus = normalizeVerificationStatus(
    publicDoctor?.verified_status ||
      doctorProfile?.verified_status ||
      fallbackDoctor?.verificationStatus,
    fallbackDoctor?.verified ? "verified" : "pending"
  );

  return {
    id: doctorId || rawDoctorId || null,
    name: pickDoctorDisplayName(
      doctorProfile?.full_name,
      authProfile?.display_name,
      publicDoctor?.display_name,
      fallbackDoctor?.name,
      "Doctor"
    ),
    specialty: pickDoctorSpecialty(
      doctorProfile?.primary_specialty,
      doctorProfile?.specialization,
      doctorProfile?.specialty,
      publicDoctor?.specialty,
      fallbackDoctor?.specialty
    ),
    avatar: resolveDoctorStorageAvatar(
      doctorProfile?.profile_image_url || doctorProfile?.avatar_url || authProfile?.avatar_url,
      doctorProfile?.profile_photo_path || publicDoctor?.profile_photo_path,
      fallbackDoctor?.avatar || "/images/demo/doctors/avatar-fallback.jpg"
    ),
    yearsExperience: formatDoctorYears(
      doctorProfile?.years_experience ||
        doctorProfile?.years_of_experience ||
        publicDoctor?.years_of_experience ||
        fallbackDoctor?.yearsExperience ||
        fallbackDoctor?.years
    ),
    rating: formatDoctorRating(doctorProfile?.rating || fallbackDoctor?.rating),
    verified:
      verificationStatus === "verified" ||
      Boolean(fallbackDoctor?.verified),
    verificationStatus:
      verificationStatus || (fallbackDoctor?.verified ? "verified" : "pending"),
    hospital:
      facility?.name ||
      publicDoctor?.facility_name ||
      fallbackDoctor?.hospital ||
      "",
    address:
      [facility?.address, facility?.city, facility?.country].filter(Boolean).join(", ") ||
      publicDoctor?.facility_address ||
      fallbackDoctor?.address ||
      "",
  };
}

function pickDoctorServiceForMode(services, consultationType) {
  const list = Array.isArray(services) ? services : [];
  if (list.length === 0) return null;

  const isEVisit = String(consultationType || "").toLowerCase() === "private";
  const score = (service) => {
    const name = String(service?.name || "").trim().toLowerCase();
    const hasVirtualSignal = /(e-?visit|virtual|video|tele)/i.test(name);
    if (isEVisit && hasVirtualSignal) return 3;
    if (!isEVisit && !hasVirtualSignal) return 2;
    if (!isEVisit && hasVirtualSignal) return 1;
    return 0;
  };

  return [...list].sort((a, b) => score(b) - score(a))[0] || list[0];
}

function resolveDoctorServiceContext(row, storedDraft, doctorContext) {
  const bookedServiceName = String(row?.booked_service_name || "").trim();
  const bookedServiceId = String(row?.booked_service_id || "").trim();
  const bookedServiceCurrency = String(row?.booked_service_currency || "").trim();
  const bookedServicePrice = Number(row?.booked_service_price);
  const hasBookedServicePrice = Number.isFinite(bookedServicePrice);
  const rawDoctorId = String(row?.doctor_id || "").trim();
  const doctorId = doctorContext.profileIdToUserId.get(rawDoctorId) || rawDoctorId;
  const services = doctorContext.servicesByDoctorId.get(doctorId) || [];
  const matchedBookedServiceById =
    services.find((service) => String(service?.id || "").trim() === bookedServiceId) || null;
  const matchedBookedServiceByName =
    bookedServiceName
      ? services.find(
          (service) =>
            String(service?.name || "").trim().toLowerCase() ===
            bookedServiceName.toLowerCase()
        ) || null
      : null;
  const matchedBookedService = matchedBookedServiceById || matchedBookedServiceByName || null;
  const resolvedBookedServiceName =
    bookedServiceName &&
    !/^(consult(?:ation|ancy)|first time consultancy)$/i.test(bookedServiceName)
      ? bookedServiceName
      : String(matchedBookedService?.name || "").trim();
  const resolvedBookedServiceCurrency =
    bookedServiceCurrency || String(matchedBookedService?.currency || "").trim() || null;
  const resolvedBookedServicePrice = hasBookedServicePrice
    ? bookedServicePrice
    : Number.isFinite(Number(matchedBookedService?.price))
      ? Number(matchedBookedService.price)
      : null;
  const doctorProfile =
    doctorContext.doctorProfilesByDoctorId?.get(doctorId) ||
    doctorContext.doctorProfilesByDoctorId?.get(rawDoctorId) ||
    null;
  const consultationFees = parseFeesObject(doctorProfile?.consultation_fees);

  if (bookedServiceName || bookedServiceId || hasBookedServicePrice || matchedBookedService) {
    return {
      serviceName: resolvedBookedServiceName,
      serviceNamePrice: Number.isFinite(resolvedBookedServicePrice)
        ? formatAmount(resolvedBookedServicePrice, resolvedBookedServiceCurrency || "ZAR")
        : "",
      amountPaid: Number.isFinite(resolvedBookedServicePrice)
        ? resolvedBookedServicePrice
        : null,
      currency: resolvedBookedServiceCurrency,
    };
  }

  const storedServiceId = String(
    firstNonEmpty([storedDraft?.serviceId, storedDraft?.payload?.serviceId, ""], "")
  ).trim();
  const storedServiceName = String(
    firstNonEmpty(
      [
        storedDraft?.serviceName,
        storedDraft?.service_name,
        storedDraft?.serviceLabel,
        storedDraft?.payload?.serviceName,
        "",
      ],
      ""
    )
  ).trim();
  const storedCurrency = String(
    firstNonEmpty(
      [storedDraft?.currency, storedDraft?.doctor?.currency, ""],
      ""
    )
  ).trim();
  const storedAmount = firstNonEmpty(
    [storedDraft?.amountPaid, storedDraft?.amount_paid, storedDraft?.serviceNamePrice, storedDraft?.serviceLabelPrice],
    null
  );
  const parsedStoredAmount = parseAmount(storedAmount);
  const storedPriceText = String(
    firstNonEmpty([storedDraft?.serviceNamePrice, storedDraft?.serviceLabelPrice], "")
  ).trim();

  let matchedService =
    services.find((service) => String(service?.id || "").trim() === storedServiceId) || null;
  let matchSource = matchedService ? "service_id" : "";

  if (!matchedService && storedServiceName) {
    const normalizedStoredName = storedServiceName.toLowerCase();
    matchedService =
      services.find(
        (service) => String(service?.name || "").trim().toLowerCase() === normalizedStoredName
      ) || null;
    if (matchedService) matchSource = "service_name";
  }

  if (!matchedService && parsedStoredAmount != null) {
    matchedService =
      services.find((service) => Number(service?.price) === parsedStoredAmount) || null;
    if (matchedService) matchSource = "service_price";
  }

  if (!matchedService) {
    matchedService = pickDoctorServiceForMode(services, row?.consultation_type);
    if (matchedService) matchSource = "mode_guess";
  }

  if (!matchedService && String(row?.consultation_type || "").toLowerCase() === "private") {
    const evisitAmount =
      pickFeeFromFeesObject(consultationFees, "evisit") ??
      parseAmount(doctorProfile?.consultation_fee);
    const evisitCurrency =
      pickCurrencyFromFeesObject(consultationFees, "evisit") || storedCurrency || "ZAR";
    return {
      serviceName: storedServiceName || "Virtual",
      serviceNamePrice: evisitAmount != null ? formatAmount(evisitAmount, evisitCurrency) : "",
      amountPaid: evisitAmount != null ? evisitAmount : null,
      currency: evisitCurrency,
    };
  }

  const matchedAmount = Number(matchedService?.price);
  const matchedCurrency = String(matchedService?.currency || "").trim();
  const matchedName = String(matchedService?.name || "").trim();
  const hasExactDbMatch =
    matchSource === "service_id" ||
    matchSource === "service_name" ||
    matchSource === "service_price";
  const resolvedCurrency = hasExactDbMatch
    ? matchedCurrency || storedCurrency || null
    : storedCurrency || matchedCurrency || null;
  const amountPaid = hasExactDbMatch
    ? Number.isFinite(matchedAmount)
      ? matchedAmount
      : parsedStoredAmount
    : parsedStoredAmount != null
      ? parsedStoredAmount
      : Number.isFinite(matchedAmount)
        ? matchedAmount
        : null;
  const priceText = hasExactDbMatch
    ? formatAmount(amountPaid, resolvedCurrency || "ZAR")
    : storedPriceText || formatAmount(amountPaid, resolvedCurrency || "ZAR");
  const serviceName = hasExactDbMatch
    ? matchedName || storedServiceName || ""
    : storedServiceName || matchedName || "";

  return {
    serviceName,
    serviceNamePrice: priceText,
    amountPaid,
    currency: resolvedCurrency,
  };
}

function resolveStorageAvatar(url, path, fallback = "/icons/circular-avatar-placeholder.svg") {
  const direct = String(url || "").trim();
  if (direct) return direct;

  const cleanPath = String(path || "").trim();
  if (cleanPath) {
    if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
    const base = getSupabaseBaseUrl();
    if (base) {
      return `${base}/storage/v1/object/public/patient-profile-images/${cleanPath.replace(/^public\//, "")}`;
    }
  }

  return fallback;
}

function extractPatientPhotoPaths(patientProfile) {
  if (!patientProfile) return [];
  if (!Array.isArray(patientProfile.patient_photo_paths)) return [];
  return patientProfile.patient_photo_paths
    .map((p) => String(p || "").trim())
    .filter(Boolean);
}

function mergeProfileRow(existingRow, incomingRow) {
  const next = {
    ...(existingRow || {}),
  };

  Object.entries(incomingRow || {}).forEach(([key, value]) => {
    if (value === 0 || value === false) {
      next[key] = value;
      return;
    }

    if (value == null) return;
    if (typeof value === "string" && value.trim() === "") return;

    next[key] = value;
  });

  return {
    ...next,
    patient_photo_paths:
      extractPatientPhotoPaths(incomingRow).length > 0
        ? extractPatientPhotoPaths(incomingRow)
        : extractPatientPhotoPaths(existingRow),
  };
}

function firstNested(value) {
  if (Array.isArray(value)) return value[0] || null;
  return value || null;
}

function buildPatientContext(patientRef, maps, row = null) {
  const patientsById = maps.patientsById;
  const patientsByUserId = maps.patientsByUserId;
  const patientProfilesByUserId = maps.patientProfilesByUserId;
  const patientProfilesByPatientId = maps.patientProfilesByPatientId;
  const authProfilesByUserId = maps.authProfilesByUserId;
  const joinedPatientByAppointmentId = maps.joinedPatientByAppointmentId;
  const joinedPatientByUserId = maps.joinedPatientByUserId;

  const nestedPatient = firstNested(row?.patient);
  const nestedAuthProfile = firstNested(nestedPatient?.profiles);

  const appointmentScoped =
    maps.currentAppointmentId != null
      ? joinedPatientByAppointmentId.get(String(maps.currentAppointmentId))
      : null;

  const fromPatientsId = patientsById.get(patientRef) || null;
  const fromPatientsUser = patientsByUserId.get(patientRef) || null;

  const patientRowId =
    nestedPatient?.id ||
    appointmentScoped?.patientProfileId ||
    fromPatientsId?.id ||
    fromPatientsUser?.id ||
    null;
  const patientUserId =
    String(nestedPatient?.user_id || "").trim() ||
    appointmentScoped?.patientUserId ||
    fromPatientsId?.user_id ||
    fromPatientsUser?.user_id ||
    patientRef ||
    null;

  const patientProfile = [
    appointmentScoped?.patientProfile || null,
    joinedPatientByUserId.get(String(patientUserId || "").trim()) || null,
    patientProfilesByPatientId.get(patientRowId) || null,
    patientProfilesByPatientId.get(patientRef) || null,
    patientProfilesByUserId.get(patientUserId) || null,
  ].reduce((merged, nextRow) => mergeProfileRow(merged, nextRow), null);
  const authProfile =
    nestedAuthProfile ||
    appointmentScoped?.authProfile ||
    authProfilesByUserId.get(patientUserId) ||
    authProfilesByUserId.get(patientRef) ||
    null;

  const firstName = firstNonEmpty(
    [
      nestedPatient?.first_name,
      patientProfile?.first_name,
      patientProfile?.firstname,
      authProfile?.first_name,
      nestedAuthProfile?.first_name,
    ],
    ""
  );
  const lastName = firstNonEmpty(
    [
      nestedPatient?.last_name,
      patientProfile?.last_name,
      patientProfile?.lastname,
      authProfile?.last_name,
      nestedAuthProfile?.last_name,
    ],
    ""
  );
  const fallbackName = `${firstName} ${lastName}`.trim();
  const patientName = firstNonEmpty(
    [
      nestedPatient?.full_name,
      nestedPatient?.display_name,
      patientProfile?.full_name,
      patientProfile?.display_name,
      nestedAuthProfile?.full_name,
      nestedAuthProfile?.display_name,
      authProfile?.full_name,
      authProfile?.display_name,
      fallbackName,
    ],
    "Patient"
  );

  const dob = firstNonEmpty(
    [
      nestedPatient?.date_of_birth,
      patientProfile?.date_of_birth,
      patientProfile?.dateOfBirth,
      patientProfile?.dob,
      null,
    ],
    null
  );
  const age = calcAge(dob);

  const genderRaw = firstNonEmpty(
    [nestedPatient?.gender, nestedPatient?.sex, patientProfile?.gender, patientProfile?.sex, authProfile?.gender, ""],
    ""
  );
  const gender = normalizeGender(genderRaw);

  const avatar = firstNonEmpty(
    [
      resolveStorageAvatar(
        patientProfile?.profile_image_url || patientProfile?.avatar_url,
        patientProfile?.profile_photo_path || patientProfile?.avatar_path,
        ""
      ),
      nestedAuthProfile?.avatar_url,
      nestedPatient?.avatar_url,
      patientProfile?.avatar_url,
      authProfile?.avatar_url,
      "/icons/circular-avatar-placeholder.svg",
    ],
    "/icons/circular-avatar-placeholder.svg"
  );

  return {
    patientRowId,
    patientUserId,
    patientProfile,
    authProfile,
    patientName,
    age,
    gender,
    dob,
    avatar,
    verificationStatus: normalizeVerificationStatus(
      firstNonEmpty(
        [
          patientProfile?.id_verification_status,
          patientProfile?.verification_status,
          authProfile?.id_verification_status,
          authProfile?.verification_status,
          "",
        ],
        ""
      ),
      ""
    ),
    patientPhotoPaths: extractPatientPhotoPaths(patientProfile),
  };
}

function buildAppointmentSnapshot(row, patientContext, options = {}) {
  const start = hhmmFromDate(row?.appointment_date);
  const duration = Number(row?.duration_minutes || 0);
  const dateISO = toDateKeyLocal(row?.appointment_date);
  const end = plusMinutesHHMM(start, duration || 15);
  const subtitle = options.subtitle || consultationLabel(row?.consultation_type);
  const rawStatus = String(row?.status || "scheduled").toLowerCase();
  const status = rawStatus || "scheduled";

  const patientPhotos = patientContext.patientPhotoPaths.map((path, idx) => ({
    id: `path-${row?.id || "appt"}-${idx}`,
    path,
    url: path,
  }));

  const snapshot = {
    id: String(row?.id || ""),
    appointmentId: String(row?.id || ""),
    doctor_id: row?.doctor_id || null,
    practice_id: row?.practice_id || null,
    patientId: patientContext.patientUserId || null,
    patient_id: patientContext.patientUserId || null,
    patient_user_id: patientContext.patientUserId || null,
    patient_row_id: patientContext.patientRowId || null,
    status,
    consultation_type: row?.consultation_type || null,
    subtitle,
    appointment_type: subtitle,
    booked_service_id: row?.booked_service_id || null,
    booked_service_name: row?.booked_service_name || null,
    booked_service_price:
      Number.isFinite(Number(row?.booked_service_price)) ? Number(row.booked_service_price) : null,
    booked_service_currency: row?.booked_service_currency || null,
    serviceName: options.serviceName || subtitle,
    serviceLabel: options.serviceName || subtitle,
    serviceNamePrice: options.serviceNamePrice || "",
    mode: String(row?.consultation_type || "").toLowerCase() === "private" ? "evisit" : "inperson",
    dateISO,
    selectedDate: dateISO,
    start,
    selectedTime: start,
    end,
    notes: row?.notes || "",
    when: {
      dateISO,
      time: start,
      start,
      end,
      duration_mins: duration || 15,
    },
    facilityName: options.facilityName || DEFAULT_FACILITY_NAME,
    facility: options.facility || null,
    doctor: options.doctor || null,
    patient_name: patientContext.patientName,
    patient_avatar: patientContext.avatar,
    care_subject_type:
      patientContext.careSubjectType ||
      String(row?.care_subject_type || "").trim().toLowerCase() ||
      "self",
    care_subject_dependant_id:
      patientContext.dependantId || row?.care_subject_dependant_id || null,
    care_subject_relationship:
      patientContext.relationship || row?.care_subject_relationship || null,
    guardian_user_id:
      patientContext.guardianUserId || row?.guardian_user_id || patientContext.patientUserId || null,
    guardian_name:
      patientContext.guardianName || patientContext.patientName || null,
    responsible_relationship:
      patientContext.responsibleRelationship || row?.responsible_relationship || null,
    age: patientContext.age,
    sex: patientContext.gender,
    patient: {
      id: patientContext.patientUserId || null,
      user_id: patientContext.patientUserId || null,
      patient_id: patientContext.patientRowId || null,
      full_name: patientContext.patientName,
      name: patientContext.patientName,
      avatar_url: patientContext.avatar,
      profile_image_url: patientContext.avatar,
      age: patientContext.age,
      gender: patientContext.gender || null,
      sex: patientContext.gender || null,
      verification_status: patientContext.verificationStatus || null,
      id_verification_status: patientContext.verificationStatus || null,
      date_of_birth: patientContext.dob || null,
      dob: patientContext.dob || null,
      blood_type: patientContext.patientProfile?.blood_type || null,
      allergies: patientContext.patientProfile?.allergies || null,
      medical_conditions: patientContext.patientProfile?.medical_conditions || null,
      primary_language: patientContext.patientProfile?.primary_language || null,
      height_cm: patientContext.patientProfile?.height_cm || null,
      weight_kg: patientContext.patientProfile?.weight_kg || null,
      chronic_conditions: patientContext.patientProfile?.chronic_conditions || null,
      organ_donor:
        typeof patientContext.patientProfile?.organ_donor === "boolean"
          ? patientContext.patientProfile.organ_donor
          : typeof patientContext.patientProfile?.is_organ_donor === "boolean"
            ? patientContext.patientProfile.is_organ_donor
            : null,
      current_medications: patientContext.patientProfile?.current_medications || null,
      phone: patientContext.patientProfile?.phone || null,
      email: patientContext.patientProfile?.email || null,
      guardian_user_id:
        patientContext.guardianUserId || row?.guardian_user_id || patientContext.patientUserId || null,
      guardian_name:
        patientContext.guardianName || patientContext.patientName || null,
      care_subject_type:
        patientContext.careSubjectType ||
        String(row?.care_subject_type || "").trim().toLowerCase() ||
        "self",
      care_subject_dependant_id:
        patientContext.dependantId || row?.care_subject_dependant_id || null,
      care_subject_relationship:
        patientContext.relationship || row?.care_subject_relationship || null,
      responsible_relationship:
        patientContext.responsibleRelationship || row?.responsible_relationship || null,
      national_id_number: patientContext.patientProfile?.national_id_number || null,
      phone_verified_at: patientContext.patientProfile?.phone_verified_at || null,
      email_verified_at: patientContext.patientProfile?.email_verified_at || null,
      patient_photo_paths: patientContext.patientPhotoPaths,
      created_at:
        patientContext.patientProfile?.created_at ||
        patientContext.authProfile?.created_at ||
        null,
    },
    patientPhotos,
    ehrSummary: {
      primary_language: patientContext.patientProfile?.primary_language || null,
      height_cm: patientContext.patientProfile?.height_cm || null,
      weight_kg: patientContext.patientProfile?.weight_kg || null,
      blood_type: patientContext.patientProfile?.blood_type || null,
      allergies: patientContext.patientProfile?.allergies || null,
      medical_conditions: patientContext.patientProfile?.medical_conditions || null,
      chronic_conditions: patientContext.patientProfile?.chronic_conditions || null,
      organ_donor:
        typeof patientContext.patientProfile?.organ_donor === "boolean"
          ? patientContext.patientProfile.organ_donor
          : typeof patientContext.patientProfile?.is_organ_donor === "boolean"
            ? patientContext.patientProfile.is_organ_donor
            : null,
      current_medications: patientContext.patientProfile?.current_medications || null,
    },
    meetUrl: options.meetUrl || "https://meet.google.com/hai-wgqq-pg",
    video_status: row?.video_status || null,
    video_started_at: row?.video_started_at || null,
    video_ended_at: row?.video_ended_at || null,
    amountPaid: options.amountPaid ?? null,
    currency: options.currency || null,
    is_paid: row?.is_paid ?? null,
    prescriptionId: options.prescriptionId || null,
    prescriptionStatus: options.prescriptionStatus || null,
    prescriptionIssuedAt: options.prescriptionIssuedAt || null,
    diagnosisTitle:
      options.diagnosisTitle ||
      pickDiagnosisTitle(row?.diagnosis_title, row?.diagnosisTitle) ||
      "",
    diagnosis:
      options.diagnosisTitle ||
      pickDiagnosisTitle(row?.diagnosis_title, row?.diagnosisTitle) ||
      "",
    diagnosisNotes:
      options.diagnosisNotes ||
      pickDiagnosisNotes(row?.diagnosis_notes, row?.diagnosisNotes) ||
      "",
    prescription: options.prescription || null,
  };

  return snapshot;
}

function buildFallbackAppointmentSnapshot(row, options = {}) {
  const start = hhmmFromDate(row?.appointment_date);
  const duration = Number(row?.duration_minutes || 15) || 15;
  const dateISO = toDateKeyLocal(row?.appointment_date);
  const end = plusMinutesHHMM(start, duration);
  const consultationType = String(row?.consultation_type || "").toLowerCase();
  const bookedServiceName = String(row?.booked_service_name || "").trim();
  const subtitle =
    bookedServiceName && !/^(consult(?:ation|ancy)|first time consultancy)$/i.test(bookedServiceName)
      ? bookedServiceName
      : consultationLabel(row?.consultation_type);
  const patientName = String(options.patientName || "").trim() || "Patient";
  const patientAvatar = String(options.patientAvatar || "").trim() || PATIENT_AVATAR_PLACEHOLDER;
  const facilityName = String(options.facilityName || "").trim() || DEFAULT_FACILITY_NAME;
  const appointmentId = String(row?.id || "").trim();

  return {
    id: appointmentId,
    appointmentId,
    doctor_id: row?.doctor_id || null,
    practice_id: row?.practice_id || null,
    patientId: options.patientUserId || null,
    patient_id: options.patientUserId || null,
    patient_user_id: options.patientUserId || null,
    patient_row_id: options.patientRowId || null,
    status: String(row?.status || "scheduled").toLowerCase(),
    consultation_type: row?.consultation_type || null,
    subtitle,
    appointment_type: subtitle,
    serviceName: bookedServiceName || subtitle,
    serviceLabel: bookedServiceName || subtitle,
    serviceNamePrice: "",
    mode: consultationType === "private" ? "evisit" : "inperson",
    dateISO,
    selectedDate: dateISO,
    start,
    selectedTime: start,
    end,
    notes: row?.notes || "",
    when: {
      dateISO,
      time: start,
      start,
      end,
      duration_mins: duration,
    },
    facilityName,
    facility: {
      id: row?.practice_id || null,
      name: facilityName,
      address: "",
      city: "",
      country: "",
    },
    patient_name: patientName,
    patient_avatar: patientAvatar,
    care_subject_type: String(row?.care_subject_type || options.careSubjectType || "self").trim().toLowerCase() || "self",
    care_subject_dependant_id: row?.care_subject_dependant_id || options.careSubjectId || null,
    care_subject_relationship: options.careSubjectRelationship || row?.care_subject_relationship || null,
    guardian_user_id: options.guardianUserId || row?.guardian_user_id || options.patientUserId || null,
    guardian_name: options.guardianName || patientName,
    responsible_relationship:
      options.responsibleRelationship || row?.responsible_relationship || null,
    age: options.age ?? null,
    sex: options.gender || "",
    patient: {
      id: options.patientUserId || null,
      user_id: options.patientUserId || null,
      patient_id: options.patientRowId || null,
      full_name: patientName,
      name: patientName,
      avatar_url: patientAvatar,
      profile_image_url: patientAvatar,
      guardian_user_id: options.guardianUserId || row?.guardian_user_id || options.patientUserId || null,
      guardian_name: options.guardianName || patientName,
      care_subject_type:
        String(row?.care_subject_type || options.careSubjectType || "self").trim().toLowerCase() || "self",
      care_subject_dependant_id: row?.care_subject_dependant_id || options.careSubjectId || null,
      care_subject_relationship: options.careSubjectRelationship || row?.care_subject_relationship || null,
      responsible_relationship:
        options.responsibleRelationship || row?.responsible_relationship || null,
      age: options.age ?? null,
      gender: options.gender || null,
      sex: options.gender || null,
      date_of_birth: options.dob || null,
      dob: options.dob || null,
      patient_photo_paths: [],
    },
    patientPhotos: [],
    meetUrl: extractConsultationMeetUrl(row, null),
    amountPaid: null,
    currency: null,
    is_paid: typeof row?.is_paid === "boolean" ? row.is_paid : false,
    status_reason: String(row?.status_reason || "").trim().toLowerCase(),
    superseded_by_appointment_id: row?.superseded_by_appointment_id || null,
    appointment_date: row?.appointment_date || null,
  };
}

function buildCanonicalDoctorAppointmentsFeed(rows, facilityName) {
  const items = [];
  const byDate = {};
  const snapshotsById = {};

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const patientName = String(
      row?.care_subject_name || row?.patient_name || "Patient"
    ).trim() || "Patient";
    const snapshot = buildFallbackAppointmentSnapshot(row, {
      patientName,
      patientUserId: row?.patient_id || null,
      guardianUserId: row?.guardian_user_id || null,
      careSubjectType: row?.care_subject_type || "self",
      careSubjectId: row?.care_subject_dependant_id || null,
      careSubjectRelationship: row?.care_subject_relationship || null,
      responsibleRelationship: row?.responsible_relationship || null,
      dob: row?.care_subject_date_of_birth || null,
      age: calcAge(row?.care_subject_date_of_birth),
      facilityName,
    });
    const appointmentId = String(row?.id || "").trim();
    const dateISO = String(snapshot?.dateISO || "").trim();
    if (!appointmentId || !dateISO) return;

    snapshotsById[appointmentId] = snapshot;
    const item = {
      id: appointmentId,
      appointmentId,
      patient_name: patientName,
      patient_age: snapshot.age,
      patient_sex: snapshot.sex,
      subtitle: snapshot.subtitle,
      start: snapshot.start,
      duration_mins: snapshot.when.duration_mins,
      icon: consultationIcon(row),
      avatar: snapshot.patient_avatar,
      status: snapshot.status,
      dateISO,
      payload: snapshot,
    };

    if (!byDate[dateISO]) byDate[dateISO] = [];
    byDate[dateISO].push(item);
    items.push(item);
  });

  Object.keys(byDate).forEach((key) => {
    byDate[key].sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));
  });

  return { items, byDate, snapshotsById };
}

function extractConsultationMeetUrl(row, fallback = null) {
  const nestedConsultation = Array.isArray(row?.consultation)
    ? row.consultation[0]
    : row?.consultation || null;

  const candidates = [
    fallback,
    row?.meetUrl,
    row?.meet_url,
    row?.meetingUrl,
    row?.meeting_url,
    nestedConsultation?.google_meet_link,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value) return value;
  }

  return null;
}

async function loadConsultationMeetUrlMap(supabase, appointmentIds) {
  const ids = Array.from(
    new Set(
      (Array.isArray(appointmentIds) ? appointmentIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (ids.length === 0) return new Map();

  try {
    const { data, error } = await supabase
      .from("consultations")
      .select("appointment_id,google_meet_link")
      .in("appointment_id", ids);

    if (error) throw error;

    return new Map(
      (Array.isArray(data) ? data : [])
        .map((row) => [String(row?.appointment_id || "").trim(), String(row?.google_meet_link || "").trim()])
        .filter(([appointmentId, meetUrl]) => !!appointmentId && !!meetUrl)
    );
  } catch (error) {
    console.warn("loadConsultationMeetUrlMap fallback:", error?.message || error);
    return new Map();
  }
}

async function loadPatientMaps(supabase, appointments) {
  const patientsById = new Map();
  const patientsByUserId = new Map();
  const patientProfilesByUserId = new Map();
  const patientProfilesByPatientId = new Map();
  const authProfilesByUserId = new Map();
  const joinedPatientByAppointmentId = new Map();
  const joinedPatientByUserId = new Map();

  const mergePatientProfile = (userId, patch, patientId = null) => {
    const key = String(userId || "").trim();
    const patientKey = String(patientId || "").trim();
    if (!key && !patientKey) return;
    if (!patch || typeof patch !== "object") return;

    const normalizedPatch = {
      ...patch,
      chronic_conditions:
        normalizeConditions(patch?.chronic_conditions) ||
        normalizeConditions(patch?.medical_conditions) ||
        null,
      medical_conditions:
        normalizeConditions(patch?.medical_conditions) ||
        normalizeConditions(patch?.chronic_conditions) ||
        null,
      organ_donor:
        coerceBoolean(patch?.organ_donor) ??
        coerceBoolean(patch?.is_organ_donor),
      is_organ_donor:
        coerceBoolean(patch?.is_organ_donor) ??
        coerceBoolean(patch?.organ_donor),
    };

    const mergeMeaningful = (current) => {
      const next = {
        ...(current || {}),
      };

      Object.entries(normalizedPatch).forEach(([field, value]) => {
        if (value === 0 || value === false) {
          next[field] = value;
          return;
        }

        if (value == null) return;
        if (typeof value === "string" && value.trim() === "") return;

        next[field] = value;
      });

      return next;
    };

    if (key) {
      const current = patientProfilesByUserId.get(key) || {};
      patientProfilesByUserId.set(key, {
        ...mergeMeaningful(current),
        user_id: key,
        patient_id: patientKey || current?.patient_id || normalizedPatch?.patient_id || null,
      });
    }

    if (patientKey) {
      const current = patientProfilesByPatientId.get(patientKey) || {};
      patientProfilesByPatientId.set(patientKey, {
        ...mergeMeaningful(current),
        patient_id: patientKey,
        user_id: key || current?.user_id || normalizedPatch?.user_id || null,
      });
    }
  };

  try {
    const patientRefs = Array.from(
      new Set(
        (Array.isArray(appointments) ? appointments : [])
          .map((row) => String(row?.patient_id || "").trim())
          .filter(Boolean)
      )
    );

    if (patientRefs.length > 0) {
      const [{ data: patientsByIdRows }, { data: patientsByUserRows }] = await Promise.all([
        supabase
          .from("patients")
          .select("id, user_id")
          .in("id", patientRefs),
        supabase
          .from("patients")
          .select("id, user_id")
          .in("user_id", patientRefs),
      ]);

      (Array.isArray(patientsByIdRows) ? patientsByIdRows : []).forEach((row) => {
        const rowId = String(row?.id || "").trim();
        const rowUserId = String(row?.user_id || "").trim();
        if (rowId) patientsById.set(rowId, row);
        if (rowUserId) patientsByUserId.set(rowUserId, row);
      });
      (Array.isArray(patientsByUserRows) ? patientsByUserRows : []).forEach((row) => {
        const rowId = String(row?.id || "").trim();
        const rowUserId = String(row?.user_id || "").trim();
        if (rowId) patientsById.set(rowId, row);
        if (rowUserId) patientsByUserId.set(rowUserId, row);
      });
    }

    const patientUserIds = Array.from(
      new Set(
        (Array.isArray(appointments) ? appointments : [])
          .map((row) => {
            const nestedPatient = firstNested(row?.patient);
            const nestedUserId = String(nestedPatient?.user_id || "").trim();
            if (nestedUserId) return nestedUserId;

            const ref = String(row?.patient_id || "").trim();
            const byId = patientsById.get(ref);
            const byUser = patientsByUserId.get(ref);
            return String(byId?.user_id || byUser?.user_id || ref || "").trim();
          })
          .filter(Boolean)
      )
    );
    const patientRowIds = Array.from(
      new Set(
        (Array.isArray(appointments) ? appointments : [])
          .map((row) => {
            const nestedPatient = firstNested(row?.patient);
            const nestedPatientId = String(nestedPatient?.id || "").trim();
            if (nestedPatientId) return nestedPatientId;

            const ref = String(row?.patient_id || "").trim();
            const byId = patientsById.get(ref);
            const byUser = patientsByUserId.get(ref);
            return String(byId?.id || byUser?.id || "").trim();
          })
          .filter(Boolean)
      )
    );

    const profileLookupIds = Array.from(new Set([...patientUserIds, ...patientRefs].filter(Boolean)));

    if (profileLookupIds.length > 0) {
      const [patientProfilesRes, authProfilesByUserRes, authProfilesByIdRes, ehrPatientInfoRes] = await Promise.all([
        supabase
          .from("patient_profiles")
          .select("*")
          .in("user_id", profileLookupIds),
        supabase
          .from("profiles")
          .select("*")
          .in("user_id", profileLookupIds),
        supabase
          .from("profiles")
          .select("*")
          .in("id", profileLookupIds),
        supabase
          .from("ehr_patient_info")
          .select(
            "patient_id, date_of_birth, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor, created_at, updated_at"
          )
          .in("patient_id", patientUserIds),
      ]);

      (Array.isArray(patientProfilesRes?.data) ? patientProfilesRes.data : []).forEach((row) => {
        const rowUserId = String(row?.user_id || "").trim();
        const patientLink = patientsByUserId.get(rowUserId) || null;
        mergePatientProfile(rowUserId, row, patientLink?.id || null);
      });

      (Array.isArray(ehrPatientInfoRes?.data) ? ehrPatientInfoRes.data : []).forEach((row) => {
        const patientUserId = String(row?.patient_id || "").trim();
        const patientLink = patientsByUserId.get(patientUserId) || null;
        const patientRowId = String(patientLink?.id || "").trim();
        mergePatientProfile(patientUserId, {
          date_of_birth: row?.date_of_birth || null,
          primary_language: row?.primary_language || null,
          height_cm: row?.height_cm ?? null,
          weight_kg: row?.weight_kg ?? null,
          blood_type: row?.blood_type || null,
          current_medications: row?.current_medications || null,
          allergies: row?.allergies || null,
          chronic_conditions: row?.chronic_conditions || null,
          is_organ_donor:
            typeof row?.is_organ_donor === "boolean" ? row.is_organ_donor : null,
          organ_donor:
            typeof row?.is_organ_donor === "boolean" ? row.is_organ_donor : null,
          created_at: row?.updated_at || row?.created_at || null,
        }, patientRowId);
      });

      [
        ...(Array.isArray(authProfilesByUserRes?.data) ? authProfilesByUserRes.data : []),
        ...(Array.isArray(authProfilesByIdRes?.data) ? authProfilesByIdRes.data : []),
      ].forEach((row) => {
        const rowUserId = String(row?.user_id || "").trim();
        const rowId = String(row?.id || "").trim();
        if (rowUserId) authProfilesByUserId.set(rowUserId, row);
        if (rowId && !authProfilesByUserId.has(rowId)) authProfilesByUserId.set(rowId, row);
      });
    }

  } catch (error) {
    console.warn("loadPatientMaps fallback:", error?.message || error);
  }

  return {
    patientsById,
    patientsByUserId,
    patientProfilesByUserId,
    patientProfilesByPatientId,
    authProfilesByUserId,
    joinedPatientByAppointmentId,
    joinedPatientByUserId,
  };
}

async function loadDoctorContext(supabase, appointments) {
  try {
    const rawDoctorIds = Array.from(
      new Set(
        (Array.isArray(appointments) ? appointments : [])
          .map((row) => String(row?.doctor_id || "").trim())
          .filter(Boolean)
      )
    );
    const practiceIds = Array.from(
      new Set(
        (Array.isArray(appointments) ? appointments : [])
          .map((row) => String(row?.practice_id || "").trim())
          .filter(Boolean)
      )
    );

    if (rawDoctorIds.length === 0) {
      return {
        profileIdToUserId: new Map(),
        doctorProfilesByDoctorId: new Map(),
        authProfilesByDoctorId: new Map(),
        publicDoctorsByDoctorId: new Map(),
        servicesByDoctorId: new Map(),
        consultanciesById: new Map(),
        firstConsultancyByDoctor: new Map(),
        legacyByDoctor: new Map(),
      };
    }

    const [
      { data: profilesByUserId },
      { data: profilesByProfileId },
    ] = await Promise.all([
      supabase
        .from("doctor_profiles")
        .select("id,user_id,full_name,primary_specialty,specialization,specialty,consultation_fee,consultation_fees,profile_image_url,profile_photo_path,verified_status,years_experience,years_of_experience,rating")
        .in("user_id", rawDoctorIds),
      supabase
        .from("doctor_profiles")
        .select("id,user_id,full_name,primary_specialty,specialization,specialty,consultation_fee,consultation_fees,profile_image_url,profile_photo_path,verified_status,years_experience,years_of_experience,rating")
        .in("id", rawDoctorIds),
    ]);

    const profileRows = [
      ...(Array.isArray(profilesByUserId) ? profilesByUserId : []),
      ...(Array.isArray(profilesByProfileId) ? profilesByProfileId : []),
    ];
    const profileIdToUserId = new Map(
      profileRows
        .map((row) => [String(row?.id || "").trim(), String(row?.user_id || "").trim()])
        .filter(([profileId, userId]) => !!profileId && !!userId)
    );
    const lookupDoctorIds = Array.from(
      new Set([...rawDoctorIds, ...rawDoctorIds.map((id) => profileIdToUserId.get(id) || id)].filter(Boolean))
    );
    const doctorProfilesByDoctorId = new Map();
    profileRows.forEach((row) => {
      const userId = String(row?.user_id || "").trim();
      const profileId = String(row?.id || "").trim();
      if (userId && !doctorProfilesByDoctorId.has(userId)) {
        doctorProfilesByDoctorId.set(userId, row);
      }
      if (profileId && !doctorProfilesByDoctorId.has(profileId)) {
        doctorProfilesByDoctorId.set(profileId, row);
      }
    });

    const [
      { data: authProfiles },
      { data: publicDoctors },
      { data: services },
      { data: consultanciesByDoctorId },
      { data: consultanciesByPracticeId },
      { data: legacyLocations },
    ] =
      await Promise.all([
        supabase
          .from("profiles")
          .select("user_id,display_name,avatar_url")
          .in("user_id", lookupDoctorIds),
        supabase
          .from("v_doctor_directory_browse")
          .select("doctor_user_id,display_name,specialty,verified_status,profile_photo_path,years_of_experience,facility_name,facility_address,facility_city,facility_country")
          .in("doctor_user_id", lookupDoctorIds),
        supabase
          .from("doctor_services")
          .select("id,doctor_id,name,price,currency,duration_minutes,is_active,sort_order,updated_at,created_at")
          .in("doctor_id", lookupDoctorIds)
          .eq("is_active", true)
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false }),
        supabase
          .from("doctor_consultancies")
          .select("id,doctor_id,facility_name,facility_address,facility_city,facility_country,is_primary,sort_order,updated_at")
          .in("doctor_id", lookupDoctorIds)
          .order("is_primary", { ascending: false })
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false }),
        practiceIds.length > 0
          ? supabase
              .from("doctor_consultancies")
              .select("id,doctor_id,facility_name,facility_address,facility_city,facility_country,is_primary,sort_order,updated_at")
              .in("id", practiceIds)
          : Promise.resolve({ data: [] }),
        supabase
          .from("doctor_consulting_locations")
          .select("id,doctor_id,facility_name,street_address,city,country,updated_at")
          .in("doctor_id", lookupDoctorIds)
          .order("updated_at", { ascending: false }),
      ]);

    const servicesByDoctorId = new Map();
    const authProfilesByDoctorId = new Map(
      (Array.isArray(authProfiles) ? authProfiles : [])
        .map((row) => [String(row?.user_id || "").trim(), row])
        .filter(([id]) => !!id)
    );
    const publicDoctorsByDoctorId = new Map(
      (Array.isArray(publicDoctors) ? publicDoctors : [])
        .map((row) => [String(row?.doctor_user_id || "").trim(), row])
        .filter(([id]) => !!id)
    );
    (Array.isArray(services) ? services : []).forEach((row) => {
      const rawDoctorId = String(row?.doctor_id || "").trim();
      const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
      if (!doctorId) return;
      const list = servicesByDoctorId.get(doctorId) || [];
      list.push(row);
      servicesByDoctorId.set(doctorId, list);
    });

    const consultanciesById = new Map();
    const firstConsultancyByDoctor = new Map();
    [
      ...(Array.isArray(consultanciesByDoctorId) ? consultanciesByDoctorId : []),
      ...(Array.isArray(consultanciesByPracticeId) ? consultanciesByPracticeId : []),
    ].forEach((row) => {
      const rowId = String(row?.id || "").trim();
      const rawDoctorId = String(row?.doctor_id || "").trim();
      const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
      if (rowId) consultanciesById.set(rowId, row);
      if (doctorId && !firstConsultancyByDoctor.has(doctorId)) {
        firstConsultancyByDoctor.set(doctorId, row);
      }
    });

    const legacyByDoctor = new Map();
    (Array.isArray(legacyLocations) ? legacyLocations : []).forEach((row) => {
      const rawDoctorId = String(row?.doctor_id || "").trim();
      const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
      if (doctorId && !legacyByDoctor.has(doctorId)) {
        legacyByDoctor.set(doctorId, row);
      }
    });

    return {
      profileIdToUserId,
      doctorProfilesByDoctorId,
      authProfilesByDoctorId,
      publicDoctorsByDoctorId,
      servicesByDoctorId,
      consultanciesById,
      firstConsultancyByDoctor,
      legacyByDoctor,
    };
  } catch (error) {
    console.warn("loadDoctorContext fallback:", error?.message || error);
    return {
      profileIdToUserId: new Map(),
      doctorProfilesByDoctorId: new Map(),
      authProfilesByDoctorId: new Map(),
      publicDoctorsByDoctorId: new Map(),
      servicesByDoctorId: new Map(),
      consultanciesById: new Map(),
      firstConsultancyByDoctor: new Map(),
      legacyByDoctor: new Map(),
    };
  }
}

async function loadDoctorAppointmentNameMap(supabase, appointmentIds) {
  const ids = new Set(
    (Array.isArray(appointmentIds) ? appointmentIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean)
  );
  if (ids.size === 0) return new Map();

  try {
    const { data, error } = await supabase.rpc("get_appointments_for_user");
    if (error) return new Map();

    const map = new Map();
    (Array.isArray(data) ? data : []).forEach((row) => {
      const appointmentId = String(row?.id || "").trim();
      const patientName = String(row?.patient_name || "").trim();
      if (!appointmentId || !patientName || !ids.has(appointmentId)) return;
      map.set(appointmentId, patientName);
    });
    return map;
  } catch {
    return new Map();
  }
}

function derivePrescriptionDiagnosis(row) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const diagnosisTitle = String(
    pickDiagnosisTitle(meta?.diagnosisTitle, meta?.diagnosis, row?.diagnosis) || ""
  ).trim();
  const diagnosisNotes = String(
    pickDiagnosisNotes(meta?.diagnosisBody, meta?.diagnosisNotes, row?.notes) || ""
  ).trim();

  return {
    prescriptionId: String(row?.id || "").trim() || null,
    prescriptionStatus: String(row?.status || "").trim() || null,
    prescriptionIssuedAt: row?.issued_at || row?.created_at || null,
    diagnosisTitle,
    diagnosisNotes,
    prescription:
      row && typeof row === "object"
        ? {
            id: row.id || null,
            status: row.status || null,
            issued_at: row.issued_at || null,
            diagnosis: row.diagnosis || null,
            notes: row.notes || null,
            meta,
          }
        : null,
  };
}

async function loadLatestPrescriptionByAppointmentIds(supabase, appointmentIds) {
  const ids = Array.from(
    new Set(
      (Array.isArray(appointmentIds) ? appointmentIds : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  if (ids.length === 0) return new Map();

  try {
    const { data, error } = await supabase
      .from("prescriptions")
      .select("id, appointment_id, status, issued_at, created_at, diagnosis, notes, meta")
      .in("appointment_id", ids)
      .order("issued_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });

    if (error) return new Map();

    const map = new Map();
    (Array.isArray(data) ? data : []).forEach((row) => {
      const appointmentId = String(row?.appointment_id || "").trim();
      if (!appointmentId || map.has(appointmentId)) return;
      map.set(appointmentId, derivePrescriptionDiagnosis(row));
    });
    return map;
  } catch {
    return new Map();
  }
}

export async function loadDoctorAppointmentsFeed({
  doctorId,
  limit = 500,
  includeCancelled = false,
  includeMissed = true,
  facilityName = DEFAULT_FACILITY_NAME,
  rangeStart = null,
  rangeEnd = null,
  onCanonicalFeed = null,
  supabase = defaultSupabase,
}) {
  const resolvedDoctorIds = Array.from(
    new Set(
      (Array.isArray(doctorId) ? doctorId : [doctorId])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  if (resolvedDoctorIds.length === 0) {
    return { items: [], byDate: {}, snapshotsById: {}, error: null };
  }

  try {
    const runQuery = async (withDiagnosisColumns = true) => {
      let query = supabase
        .from("appointments")
        .select(
          withDiagnosisColumns
            ? "id, patient_id, doctor_id, appointment_date, duration_minutes, status, status_reason, superseded_by_appointment_id, consultation_type, notes, diagnosis_title, diagnosis_notes, practice_id, is_paid, booked_service_id, booked_service_name, booked_service_price, booked_service_currency, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship, video_status, video_started_at, video_ended_at"
            : "id, patient_id, doctor_id, appointment_date, duration_minutes, status, status_reason, superseded_by_appointment_id, consultation_type, notes, practice_id, is_paid, booked_service_id, booked_service_name, booked_service_price, booked_service_currency, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship, video_status, video_started_at, video_ended_at"
        )
        .order("appointment_date", { ascending: true })
        .limit(limit);

      query =
        resolvedDoctorIds.length === 1
          ? query.eq("doctor_id", resolvedDoctorIds[0])
          : query.in("doctor_id", resolvedDoctorIds);

      query = query.is("superseded_by_appointment_id", null);

      if (rangeStart) {
        query = query.gte("appointment_date", rangeStart);
      }
      if (rangeEnd) {
        query = query.lt("appointment_date", rangeEnd);
      }

      if (!includeCancelled) {
        query = query.neq("status", "cancelled");
      }

      if (!includeMissed) {
        query = query.neq("status", "missed");
      }

      const result = await query;
      if (!result.error) return result;
      if (
        withDiagnosisColumns &&
        /diagnosis_title|diagnosis_notes/i.test(String(result.error?.message || ""))
      ) {
        const fallback = await runQuery(false);
        if (fallback.error) return fallback;
        return {
          ...fallback,
          data: (Array.isArray(fallback.data) ? fallback.data : []).map((row) => ({
            ...row,
            diagnosis_title: null,
            diagnosis_notes: null,
          })),
        };
      }
      return result;
    };

    const { data, error } = await runQuery(true);
    if (error) throw error;

    const rows = Array.isArray(data) ? data : [];
    const canonicalFeed = buildCanonicalDoctorAppointmentsFeed(rows, facilityName);
    if (typeof onCanonicalFeed === "function") {
      try {
        onCanonicalFeed({
          ...canonicalFeed,
          trace: {
            appointmentRequestCount: 1,
            rowCount: rows.length,
          },
        });
      } catch {
        // Rendering canonical appointment rows must never make enrichment fail.
      }
    }
    const [
      maps,
      sharedPatientContextsByAppointmentId,
      doctorContext,
      patientNamesByAppointmentId,
      consultationMeetUrlByAppointmentId,
    ] = await Promise.all([
      loadPatientMaps(supabase, rows),
      loadClinicianAppointmentPatientIdentities(rows, supabase),
      loadDoctorContext(supabase, rows),
      loadDoctorAppointmentNameMap(supabase, rows.map((row) => row?.id)),
      loadConsultationMeetUrlMap(supabase, rows.map((row) => row?.id)),
    ]);
    const pairVisitCounts = new Map();

    const snapshotsById = {};
    const byDate = {};
    const items = rows.reduce((acc, row) => {
      try {
        const draft = combineStoredAppointmentDrafts(row?.id);
        const patientRef = String(row?.patient_id || "").trim();
        const pairKey = `${String(row?.doctor_id || "").trim()}::${patientRef}`;
        const visitOrdinal = (pairVisitCounts.get(pairKey) || 0) + 1;
        pairVisitCounts.set(pairKey, visitOrdinal);

        const patientContext = mergePatientContextWithStoredDraft(
          mergeResolvedPatientContext(
            buildPatientContext(
              patientRef,
              {
                ...maps,
                currentAppointmentId: row?.id || null,
              },
              row
            ),
            sharedPatientContextsByAppointmentId.get(String(row?.id || "")) || null
          ),
          draft
        );
        const rpcPatientName = String(patientNamesByAppointmentId.get(String(row?.id || "")) || "").trim();
        const hydratedPatientContext =
          rpcPatientName && patientContext.patientName === "Patient"
            ? { ...patientContext, patientName: rpcPatientName }
            : patientContext;
        const facility = resolveFacilityContext(row, doctorContext, draft, facilityName);
        const serviceContext = resolveDoctorServiceContext(row, draft, doctorContext);
        const doctor = buildDoctorSummary(row, doctorContext, facility, draft);
        const subtitle = resolveDoctorAppointmentSubtitle(
          row,
          draft,
          visitOrdinal,
          serviceContext.serviceName
        );
        const snapshot = buildAppointmentSnapshot(row, hydratedPatientContext, {
          facilityName: facility?.name || DEFAULT_FACILITY_NAME,
          facility,
          doctor,
          subtitle,
          serviceName: serviceContext.serviceName || subtitle,
          serviceNamePrice: serviceContext.serviceNamePrice || "",
          meetUrl: extractConsultationMeetUrl(
            row,
            draft?.meetUrl ||
              draft?.meetingUrl ||
              consultationMeetUrlByAppointmentId.get(String(row?.id || "")) ||
              null
          ),
          amountPaid: serviceContext.amountPaid,
          currency: serviceContext.currency,
        });
        snapshotsById[String(row.id)] = snapshot;

        const item = {
          id: String(row.id),
          appointmentId: String(row.id),
          patient_name: hydratedPatientContext.patientName,
          patient_age: hydratedPatientContext.age,
          patient_sex: hydratedPatientContext.gender,
          subtitle: snapshot.subtitle,
          start: snapshot.start,
          duration_mins: snapshot.when.duration_mins,
          icon: consultationIcon(row),
          avatar: hydratedPatientContext.avatar,
          status: snapshot.status,
          dateISO: snapshot.dateISO,
          payload: snapshot,
        };

        const key = snapshot.dateISO;
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(item);
        acc.push(item);
      } catch (rowError) {
        const fallbackResolved =
          sharedPatientContextsByAppointmentId.get(String(row?.id || "")) || null;
        const fallbackSnapshot = buildFallbackAppointmentSnapshot(row, {
          patientName:
            fallbackResolved?.patientName ||
            patientNamesByAppointmentId.get(String(row?.id || "")) ||
            "Patient",
          patientAvatar: fallbackResolved?.avatar || fallbackResolved?.patientAvatar || PATIENT_AVATAR_PLACEHOLDER,
          patientUserId: fallbackResolved?.patientUserId || null,
          patientRowId: fallbackResolved?.patientRowId || null,
          age: fallbackResolved?.age ?? null,
          gender: fallbackResolved?.gender || "",
          dob: fallbackResolved?.dob || null,
          facilityName,
        });
        snapshotsById[String(row.id)] = fallbackSnapshot;
        const key = fallbackSnapshot.dateISO;
        const fallbackItem = {
          id: String(row.id),
          appointmentId: String(row.id),
          patient_name: fallbackSnapshot.patient_name,
          patient_age: fallbackSnapshot.age,
          patient_sex: fallbackSnapshot.sex,
          subtitle: fallbackSnapshot.subtitle,
          start: fallbackSnapshot.start,
          duration_mins: fallbackSnapshot.when.duration_mins,
          icon: consultationIcon(row),
          avatar: fallbackSnapshot.patient_avatar,
          status: fallbackSnapshot.status,
          dateISO: fallbackSnapshot.dateISO,
          payload: fallbackSnapshot,
        };
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(fallbackItem);
        acc.push(fallbackItem);
        console.warn(
          "Hydrated doctor appointment fallback row:",
          row?.id,
          rowError?.message || rowError
        );
      }
      return acc;
    }, []);

    Object.keys(byDate).forEach((key) => {
      byDate[key].sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));
    });

    return { items, byDate, snapshotsById, error: null };
  } catch (error) {
    return { items: [], byDate: {}, snapshotsById: {}, error };
  }
}

export async function loadDoctorAppointmentSnapshot({
  appointmentId,
  doctorId = null,
  facilityName = DEFAULT_FACILITY_NAME,
  supabase = defaultSupabase,
}) {
  if (!appointmentId) return null;

  try {
    const runQuery = async (withDiagnosisColumns = true) => {
      const result = await supabase
        .from("appointments")
        .select(
          withDiagnosisColumns
            ? "id, patient_id, doctor_id, appointment_date, duration_minutes, status, status_reason, superseded_by_appointment_id, consultation_type, notes, diagnosis_title, diagnosis_notes, practice_id, is_paid, booked_service_id, booked_service_name, booked_service_price, booked_service_currency, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship, video_status, video_started_at, video_ended_at"
            : "id, patient_id, doctor_id, appointment_date, duration_minutes, status, status_reason, superseded_by_appointment_id, consultation_type, notes, practice_id, is_paid, booked_service_id, booked_service_name, booked_service_price, booked_service_currency, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship, video_status, video_started_at, video_ended_at"
        )
        .eq("id", appointmentId)
        .limit(1)
        .maybeSingle();

      if (!result.error) return result;
      if (
        withDiagnosisColumns &&
        /diagnosis_title|diagnosis_notes/i.test(String(result.error?.message || ""))
      ) {
        const fallback = await runQuery(false);
        if (fallback.error || !fallback.data) return fallback;
        return {
          ...fallback,
          data: {
            ...fallback.data,
            diagnosis_title: null,
            diagnosis_notes: null,
          },
        };
      }
      return result;
    };

    const { data, error } = await runQuery(true);
    if (error) throw error;
    if (!data) return null;

    const [
      maps,
      sharedPatientContextsByAppointmentId,
      doctorContext,
      patientNamesByAppointmentId,
      consultationMeetUrlByAppointmentId,
      latestPrescriptionByAppointmentId,
    ] = await Promise.all([
      loadPatientMaps(supabase, [data]),
      loadClinicianAppointmentPatientIdentities([data], supabase),
      loadDoctorContext(supabase, [data]),
      loadDoctorAppointmentNameMap(supabase, [data?.id]),
      loadConsultationMeetUrlMap(supabase, [data?.id]),
      loadLatestPrescriptionByAppointmentIds(supabase, [data?.id]),
    ]);
    const draft = combineStoredAppointmentDrafts(data?.id);
    const patientRef = String(data?.patient_id || "").trim();
    const patientContext = mergePatientContextWithStoredDraft(
      mergeResolvedPatientContext(
        buildPatientContext(
          patientRef,
          {
            ...maps,
            currentAppointmentId: data?.id || null,
          },
          data
        ),
        sharedPatientContextsByAppointmentId.get(String(data?.id || "")) || null
      ),
      draft
    );
    const rpcPatientName = String(patientNamesByAppointmentId.get(String(data?.id || "")) || "").trim();
    const hydratedPatientContext =
      rpcPatientName && patientContext.patientName === "Patient"
        ? { ...patientContext, patientName: rpcPatientName }
        : patientContext;
    const facility = resolveFacilityContext(data, doctorContext, draft, facilityName);
    const serviceContext = resolveDoctorServiceContext(data, draft, doctorContext);
    const doctor = buildDoctorSummary(data, doctorContext, facility, draft);
    const subtitle = resolveDoctorAppointmentSubtitle(
      data,
      draft,
      1,
      serviceContext.serviceName
    );
    const latestPrescription =
      latestPrescriptionByAppointmentId.get(String(data?.id || "")) || null;
    return buildAppointmentSnapshot(data, hydratedPatientContext, {
      facilityName: facility?.name || DEFAULT_FACILITY_NAME,
      facility,
      doctor,
      subtitle,
      serviceName: serviceContext.serviceName || subtitle,
      serviceNamePrice: serviceContext.serviceNamePrice || "",
      meetUrl: extractConsultationMeetUrl(
        data,
        draft?.meetUrl ||
          draft?.meetingUrl ||
          consultationMeetUrlByAppointmentId.get(String(data?.id || "")) ||
          null
      ),
      amountPaid: serviceContext.amountPaid,
      currency: serviceContext.currency,
      prescriptionId: draft?.prescriptionId || latestPrescription?.prescriptionId || null,
      prescriptionStatus: latestPrescription?.prescriptionStatus || null,
      prescriptionIssuedAt: latestPrescription?.prescriptionIssuedAt || null,
      diagnosisTitle:
        pickDiagnosisTitle(
          data?.diagnosis_title,
          draft?.diagnosisTitle,
          draft?.diagnosis,
          latestPrescription?.diagnosisTitle,
        ) || "",
      diagnosisNotes:
        pickDiagnosisNotes(
          data?.diagnosis_notes,
          draft?.diagnosisNotes,
          latestPrescription?.diagnosisNotes,
        ) || "",
      prescription: latestPrescription?.prescription || null,
    });
  } catch {
    return null;
  }
}

export { consultationLabel, consultationIcon, toDateKeyLocal };
